/**
 * Global Event Manager
 *
 * Purpose: Prevent memory leaks with global event listeners (especially for HID scanners)
 *
 * Problem:
 *   - React 18 Strict Mode mounts components twice in development
 *   - useEffect cleanup functions don't always run correctly
 *   - Multiple event listeners can be attached to the same event
 *   - Scanner events fire multiple times
 *
 * Solution:
 *   - Centralized event management
 *   - Automatic deduplication
 *   - Priority system for conflicting handlers
 *   - Guaranteed cleanup
 *
 * Architecture Decision #3: Event Manager
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { logger } from '@/services/logger';

// ============================================================================
// Types
// ============================================================================

/**
 * Event listener configuration
 */
interface EventListenerConfig {
  id: string; // Unique identifier (e.g., "scanner:main", "keyboard:global")
  event: string; // Event name (e.g., "keydown", "keyup")
  handler: EventListener; // Event handler function
  priority: number; // Higher priority = runs first (default: 0)
  once: boolean; // Remove after first execution
  target: EventTarget; // Event target (window, document, element)
}

/**
 * Registered event listener
 */
interface RegisteredListener extends EventListenerConfig {
  registeredAt: number; // Timestamp
  executionCount: number; // How many times this handler was called
}

// ============================================================================
// Global Event Manager Class
// ============================================================================

class GlobalEventManager {
  private listeners = new Map<string, RegisteredListener>();
  private isCleaningUp = false;

  /**
   * Register a global event listener
   *
   * @param config Event listener configuration
   * @returns Cleanup function
   */
  register(
    config: Omit<EventListenerConfig, 'once' | 'priority'> &
      Partial<Pick<EventListenerConfig, 'once' | 'priority'>>
  ): () => void {
    const fullConfig: EventListenerConfig = {
      ...config,
      priority: config.priority ?? 0,
      once: config.once ?? false,
    };

    // Check if listener already exists
    if (this.listeners.has(fullConfig.id)) {
      logger.warn(`Event listener already registered: ${fullConfig.id}`, undefined, 'EventManager');
      // Return existing cleanup function
      return () => this.unregister(fullConfig.id);
    }

    // Wrap handler to track execution count
    const wrappedHandler: EventListener = (event) => {
      const listener = this.listeners.get(fullConfig.id);
      if (!listener) return;

      // Increment execution count
      listener.executionCount++;

      // Call original handler
      try {
        fullConfig.handler(event);
      } catch (error) {
        logger.error(`Event handler error: ${fullConfig.id}`, error, 'EventManager');
      }

      // Remove if "once" flag is set
      if (fullConfig.once) {
        this.unregister(fullConfig.id);
      }
    };

    // Register listener
    const registered: RegisteredListener = {
      ...fullConfig,
      handler: wrappedHandler,
      registeredAt: Date.now(),
      executionCount: 0,
    };

    this.listeners.set(fullConfig.id, registered);

    // Attach to target
    fullConfig.target.addEventListener(fullConfig.event, wrappedHandler);

    logger.debug(
      `Event listener registered: ${fullConfig.id} (${fullConfig.event})`,
      undefined,
      'EventManager'
    );

    // Return cleanup function
    return () => this.unregister(fullConfig.id);
  }

  /**
   * Unregister an event listener
   */
  unregister(id: string): void {
    const listener = this.listeners.get(id);
    if (!listener) {
      logger.warn(`Event listener not found: ${id}`, undefined, 'EventManager');
      return;
    }

    // Remove from target
    listener.target.removeEventListener(listener.event, listener.handler);

    // Remove from registry
    this.listeners.delete(id);

    logger.debug(
      `Event listener unregistered: ${id} (executed ${listener.executionCount} times)`,
      undefined,
      'EventManager'
    );
  }

  /**
   * Check if an event listener is registered
   */
  isRegistered(id: string): boolean {
    return this.listeners.has(id);
  }

  /**
   * Get statistics for a specific listener
   */
  getListenerStats(id: string) {
    const listener = this.listeners.get(id);
    if (!listener) return null;

    return {
      id: listener.id,
      event: listener.event,
      priority: listener.priority,
      registeredAt: listener.registeredAt,
      executionCount: listener.executionCount,
      age: Date.now() - listener.registeredAt,
    };
  }

  /**
   * Get all registered listeners
   */
  getAllListeners() {
    return Array.from(this.listeners.values()).map((listener) => ({
      id: listener.id,
      event: listener.event,
      priority: listener.priority,
      registeredAt: listener.registeredAt,
      executionCount: listener.executionCount,
      age: Date.now() - listener.registeredAt,
    }));
  }

  /**
   * Clear all event listeners (e.g., on logout)
   */
  clearAll(): void {
    if (this.isCleaningUp) {
      logger.warn('Already cleaning up event listeners', undefined, 'EventManager');
      return;
    }

    this.isCleaningUp = true;

    logger.info(`Clearing ${this.listeners.size} event listeners`, undefined, 'EventManager');

    // Unregister all listeners
    const ids = Array.from(this.listeners.keys());
    for (const id of ids) {
      this.unregister(id);
    }

    this.isCleaningUp = false;
  }

  /**
   * Get statistics
   */
  getStats() {
    return {
      totalListeners: this.listeners.size,
      listeners: this.getAllListeners(),
    };
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Singleton instance of the Global Event Manager
 */
export const eventManager = new GlobalEventManager();

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Register a keyboard event listener (convenience wrapper)
 *
 * @param id Unique identifier (e.g., "scanner:main")
 * @param handler Event handler
 * @param options Optional configuration
 * @returns Cleanup function
 */
export function registerKeyboardListener(
  id: string,
  handler: (event: KeyboardEvent) => void,
  options?: {
    priority?: number;
    once?: boolean;
    target?: EventTarget;
  }
): () => void {
  return eventManager.register({
    id,
    event: 'keydown',
    handler: handler as EventListener,
    target: options?.target || window,
    priority: options?.priority,
    once: options?.once,
  });
}

/**
 * Register a scanner event listener (convenience wrapper)
 *
 * Scanner events are keydown events that arrive very quickly (< 50ms between keys).
 * This helper automatically buffers keys and calls the handler with the complete barcode.
 *
 * @param id Unique identifier (e.g., "scanner:pos")
 * @param handler Barcode handler (receives complete barcode string)
 * @param options Optional configuration
 * @returns Cleanup function
 */
export function registerScannerListener(
  id: string,
  handler: (barcode: string) => void,
  options?: {
    priority?: number;
    timeout?: number; // Time to wait for next key (default: 50ms)
    minLength?: number; // Minimum barcode length (default: 4)
    target?: EventTarget;
  }
): () => void {
  let buffer = '';
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const scannerTimeout = options?.timeout ?? 50;
  const minLength = options?.minLength ?? 4;

  const keydownHandler = (event: KeyboardEvent) => {
    // Ignore if focused on input/textarea
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    // Ignore modifier keys
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    // Enter key = end of barcode
    if (event.key === 'Enter') {
      if (buffer.length >= minLength) {
        logger.debug(`Scanner detected barcode: ${buffer}`, undefined, 'EventManager');
        handler(buffer);
      }
      buffer = '';
      if (timeoutId) clearTimeout(timeoutId);
      return;
    }

    // Accumulate printable characters
    if (event.key.length === 1) {
      buffer += event.key;

      // Reset timeout
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Timeout expired, check if we have a valid barcode
        if (buffer.length >= minLength) {
          logger.debug(`Scanner detected barcode (timeout): ${buffer}`, undefined, 'EventManager');
          handler(buffer);
        }
        buffer = '';
      }, scannerTimeout);
    }
  };

  return eventManager.register({
    id,
    event: 'keydown',
    handler: keydownHandler as EventListener,
    target: options?.target || window,
    priority: options?.priority,
  });
}

// ============================================================================
// Cleanup on window unload
// ============================================================================

// Automatically clean up all event listeners when the page unloads
window.addEventListener('beforeunload', () => {
  eventManager.clearAll();
});

// Export type for use in other files
export type { EventListenerConfig, RegisteredListener };
